import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  Divider,
  FormControlLabel,
  Switch,
  Tab,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  Tabs,
} from '@mui/material';
import { HeaderBreadcrumbs, Iconify, Page, Scrollbar } from '@/components';
import {
  TableEmptyRows,
  TableHeadCustom,
  TableNoData,
} from '@/components/table';
import { SkeletonLoadingTable } from '@/components/skeleton';
import { ToolbarWithSearchAndStatus } from '@/components/toolbar';
import useSettings from '@/hooks/useSettings';
import useTable, { emptyRows, getComparator } from '@/hooks/useTable';
import useTabs from '@/hooks/useTabs';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import { PATH_USER } from '@/features/app';
import { getReferrals } from '../api';
import { ReferralTableRow } from '../components';
import { REFERRALS_ROOT } from '../routes/paths';
import { Referral, ReferralFilterValue } from '../types';
import {
  applySortFilterReferrals,
  getReferralFilterTabs,
  REFERRALS_TABLE_HEAD,
} from '../utils';

export default function ReferralsList() {
  const table = useTable({
    defaultDense: true,
    defaultOrder: 'desc',
    defaultOrderBy: 'created_at',
  });
  const { themeStretch } = useSettings();
  const { errorMsg } = useSnackbarMsg();
  const [searchFieldValue, setSearchFieldValue] = useState('');
  const {
    currentTab,
    onChangeTab: onChangeFilterTab,
    setCurrentTab,
  } = useTabs('all');

  const { data: referrals, isLoading } = useQuery({
    queryKey: ['referrals'],
    queryFn: getReferrals,
    onError: (error) => {
      errorMsg(error, 'Something went wrong while fetching referrals');
    },
  });

  const filterTabs = getReferralFilterTabs(referrals ?? []);

  useEffect(() => {
    if (!filterTabs.some((tab) => tab.value === currentTab)) {
      setCurrentTab('all');
    }
  }, [currentTab, filterTabs, setCurrentTab]);

  const handleFilterText = (value: string) => {
    setSearchFieldValue(value);
    table.setPage(0);
  };

  const handleResetFilters = () => {
    setSearchFieldValue('');
    table.setPage(0);
  };

  const handleTabChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: string
  ) => {
    onChangeFilterTab(event, value);
    table.setPage(0);
  };

  const filterStatus = currentTab as ReferralFilterValue;
  const dataFiltered = applySortFilterReferrals({
    tableData: referrals ?? [],
    comparator: getComparator<Referral>(
      table.order,
      table.orderBy as keyof Referral
    ),
    searchFieldValue,
    filterStatus,
  });
  const denseHeight = table.dense ? 52 : 72;
  const isNotFound =
    !dataFiltered.length && (!!searchFieldValue || filterStatus !== 'all');

  return (
    <Page title="Referrals / Rewards">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Referrals / Rewards"
          links={[
            { name: 'Dashboard', href: PATH_USER.root },
            { name: 'Marketing' },
            { name: 'Referrals / Rewards', href: REFERRALS_ROOT },
          ]}
        />

        <Card>
          {isLoading ? (
            <SkeletonLoadingTable />
          ) : (
            <>
              <Tabs
                allowScrollButtonsMobile
                variant="scrollable"
                scrollButtons="auto"
                value={filterStatus}
                onChange={handleTabChange}
                sx={{ px: 2, bgcolor: 'background.neutral' }}
              >
                {filterTabs.map((tab) => (
                  <Tab
                    disableRipple
                    key={tab.value}
                    value={tab.value}
                    label={
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.75,
                        }}
                      >
                        {tab.label}
                        <Chip size="small" label={tab.count} />
                      </Box>
                    }
                  />
                ))}
              </Tabs>

              <Divider />

              <ToolbarWithSearchAndStatus
                filterText={searchFieldValue}
                onFilterText={handleFilterText}
                labels={{ search: 'Search referrals...' }}
                action={
                  searchFieldValue ? (
                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={handleResetFilters}
                      startIcon={<Iconify icon="eva:close-circle-outline" />}
                    >
                      Reset
                    </Button>
                  ) : null
                }
              />

              <Scrollbar>
                <TableContainer sx={{ minWidth: 1100, position: 'relative' }}>
                  <Table size={table.dense ? 'small' : 'medium'}>
                    <TableHeadCustom
                      order={table.order}
                      orderBy={table.orderBy}
                      headLabel={REFERRALS_TABLE_HEAD}
                      rowCount={dataFiltered.length}
                      onSort={table.onSort}
                    />
                    <TableBody>
                      {dataFiltered
                        .slice(
                          table.page * table.rowsPerPage,
                          table.page * table.rowsPerPage + table.rowsPerPage
                        )
                        .map((referral) => (
                          <ReferralTableRow
                            key={String(referral.id)}
                            row={referral}
                          />
                        ))}

                      <TableEmptyRows
                        height={denseHeight}
                        emptyRows={emptyRows(
                          table.page,
                          table.rowsPerPage,
                          dataFiltered.length
                        )}
                      />

                      <TableNoData
                        isNotFound={isNotFound || !dataFiltered.length}
                        title="No referrals found"
                      />
                    </TableBody>
                  </Table>
                </TableContainer>
              </Scrollbar>

              <Box sx={{ position: 'relative' }}>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50]}
                  component="div"
                  count={dataFiltered.length}
                  rowsPerPage={table.rowsPerPage}
                  page={table.page}
                  onPageChange={table.onChangePage}
                  onRowsPerPageChange={table.onChangeRowsPerPage}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={table.dense}
                      onChange={table.onChangeDense}
                    />
                  }
                  label="Dense"
                  sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
                />
              </Box>
            </>
          )}
        </Card>
      </Container>
    </Page>
  );
}
